import React, {
  FormEventHandler,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useSelector } from "react-redux"
import {
  selectMemberId,
  selectMemberMatrixData,
} from "@/store/member/member.slice"
import {
  useGetMemberIdQuery,
  useSetMemberIdMutation,
} from "@/store/apis/linkApi"
import { useBodyContentQuery } from "@/store/apis/matrixApi"
import { useContentJsx } from "@/utils/hooks/useContent"
import MainLayoutLoader from "@components/Loader/MainLayoutLoader"
import { debounce, first } from "lodash"
import { getOktaAdminUrl } from "@/utils/helpers/configs"
import { useOktaProfileMutation } from "@/store/apis/idpApi"

const useGetProfile = () => {
  const [getProfile, { isLoading, data, error }] = useOktaProfileMutation({
    fixedCacheKey: "okta-user-profile",
  })

  const [current, setCurrent] = useState<string | undefined>()

  const oktaUrl = useMemo(() => {
    if (data && data.length) {
      // active is the preferred choice
      const active = data.find((item) => item.status === "ACTIVE")?.id

      // otherwise try to get the account that's not suspended or deprovisioned
      const picked =
        data.length > 1
          ? first(
              data.filter(
                (user) => !["SUSPENDED", "DEPROVISIONED"].includes(user.status)
              )
            )?.id
          : first(data)?.id

      return picked ? getOktaAdminUrl(active ?? picked) : undefined
    }
  }, [data])

  const triggerGetProfile = (id: string) => {
    setCurrent(id)
    getProfile({ id })
  }

  return [
    triggerGetProfile,
    {
      isLoading,
      error,
      oktaUrl,
      current,
    },
  ] as const
}

function MemberErrorMessage() {
  return <p>Member number not found, please try another number</p>
}

type MemberSwitchProps = PropsWithChildren & {
  current: string
  onChange?: (value: string) => void
  wrap?: boolean
}

function MemberSwitch({
  current,
  onChange,
  children,
  wrap = true,
}: MemberSwitchProps) {
  const [setMemberId, { isLoading }] = useSetMemberIdMutation()

  const [value, setValue] = useState(current)

  useLayoutEffect(() => {
    document.documentElement.classList.add("member-view-enabled")
    return () => {
      document.documentElement.classList.remove("member-view-enabled")
    }
  }, [])

  useEffect(() => {
    if (current) {
      setValue(current)
    }
  }, [current])

  useEffect(() => {
    if ("function" === typeof onChange) {
      onChange(value)
    }
  }, [value])

  const onSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    if (value !== current) {
      setMemberId(value)
    }
  }
  const Wrapper = ({
    children,
    wrap,
  }: Pick<MemberSwitchProps, "wrap" | "children">) =>
    wrap ? (
      <div className="bottom-btn-wrapper bottom-btn-wrapper--right">
        {children}
      </div>
    ) : (
      <>{children}</>
    )

  return (
    <>
      <form onSubmit={onSubmit}>
        <div className="member-view__login-container">
          <label>
            <span>Member number</span>
            <input
              maxLength={7}
              type="tel"
              width="100%"
              onChange={(e) => setValue(e.target.value)}
              value={value}
              disabled={isLoading}
            />
          </label>
          <Wrapper wrap={wrap}>
            <button
              type={"submit"}
              className="member-view__submit button"
              disabled={isLoading}
            >
              See Member View
            </button>
          </Wrapper>
        </div>
      </form>
      {children}
    </>
  )
}

function OktaMemberSwitch() {
  const [value, setValue] = useState("")
  const [getProfile, { isLoading, error, oktaUrl }] = useGetProfile()

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault()
    getProfile(value)
  }

  useEffect(() => {
    if (oktaUrl) {
      window.open(oktaUrl, "_blank", "noopener,noreferrer")
    }
  }, [oktaUrl])

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="member-view__login-container">
          <label>
            Member number
            <input
              maxLength={7}
              type="tel"
              width="100%"
              onChange={(e) => setValue(e.target.value)}
              value={value}
              disabled={isLoading}
            />
          </label>
          <div className="bottom-btn-wrapper bottom-btn-wrapper--right">
            <button
              type={"submit"}
              className="member-view__submit button"
              disabled={isLoading}
            >
              Manage member account in Okta
            </button>
          </div>
        </div>
      </form>
      {error && <MemberErrorMessage />}
    </>
  )
}

interface OktaMemberAccountProps {
  current: string
  pending: string
}

function OktaMemberAccount({ current, pending }: OktaMemberAccountProps) {
  const [getProfile, { oktaUrl, current: oktaCurrent }] = useGetProfile()

  useEffect(() => {
    if (current) {
      getProfile(current)
    }
  }, [current])

  if (oktaUrl && oktaCurrent === current && pending === current) {
    return (
      <a className="button" href={oktaUrl} target="_blank">
        Manage member account
      </a>
    )
  }

  return null
}

interface MemberViewPanelProps {
  memberId: string
}

function MemberViewPanel({ memberId }: MemberViewPanelProps) {
  const member = useSelector(selectMemberMatrixData)
  const current = memberId === "pending" ? "" : memberId ?? ""
  const [pending, setPending] = useState<string>("")

  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const page = document.querySelector<HTMLDivElement>(".page-wrapper")!
    const onResize = debounce(() => {
      const bottom = ref.current?.getBoundingClientRect().bottom
      page.style.setProperty("position", "relative")
      page.style.setProperty("top", bottom + "px")
    }, 150)

    window.addEventListener("resize", onResize)

    onResize()

    return () => {
      onResize.cancel()
      page.style.setProperty("position", null)
      page.style.setProperty("top", null)
      window.removeEventListener("resize", onResize)
    }
  }, [])

  return (
    <div ref={ref} className="member-view">
      {current && (
        <>
          <div className="member-switch__header">
            <MemberSwitch current={current} onChange={setPending} wrap={false}>
              <OktaMemberAccount current={current} pending={pending} />
            </MemberSwitch>
          </div>
        </>
      )}
      <div className="member-view__rightpanel">
        <span className="member-view__name">{member.name}</span>
      </div>
    </div>
  )
}

function MemberViewNotice() {
  const {
    contentHeading: memberViewHeading,
    contentBody: memberViewContent,
    contentFooter: memberViewFooter,
  } = useContentJsx(`member_view.member_view`)

  const { contentBody: memberViewGuidelines } = useContentJsx(
    "member_view.using_member_view"
  )

  const {
    contentHeading: oktaMemberViewHeading,
    contentBody: oktaMemberViewContent,
  } = useContentJsx(`member_view.okta_manage_member_account`)

  return (
    <div className="member-view-notice">
      <div className="mol-component mol-component--thick">
        <div className="layout-twocol layout-twocol--rightweight">
          <div className="layout-twocol__column">
            <div className="mol-component">
              <div className="mol-component__content">
                <h2>{memberViewHeading}</h2>
                <div className="member-switch__content">
                  <MemberSwitch current="" />
                </div>
                {memberViewContent}
                {memberViewFooter}
              </div>
              <div className="mol-component__content">
                {memberViewGuidelines}
              </div>
            </div>
          </div>

          <div className="layout-twocol__column member-view-notice__okta">
            <div className="mol-component">
              <div className="mol-component__content">
                <h2>{oktaMemberViewHeading}</h2>
                <div className="member-switch__content">
                  <OktaMemberSwitch />
                </div>
                {oktaMemberViewContent}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface WrapperProps {
  element: ReactNode
}

function Wrapper({ element }: WrapperProps) {
  return (
    <main className="main" id="main">
      <div className="content-container">{element}</div>
    </main>
  )
}

type MemberViewProps = PropsWithChildren & {
  resetError?(): void
}

function MemberView({ children, resetError }: MemberViewProps) {
  const memberId = useSelector(selectMemberId)
  const [initialMid] = useState(memberId)

  const { isFetching: isFetchingMemberId } = useGetMemberIdQuery()
  const { isFetching: isFetchingContent } = useBodyContentQuery()

  useEffect(() => {
    if ("function" === typeof resetError && memberId !== initialMid) {
      resetError()
    }
  }, [memberId])

  children =
    isFetchingMemberId || isFetchingContent || memberId === "pending" ? (
      <Wrapper element={<MainLayoutLoader />} />
    ) : !memberId ? (
      <Wrapper element={<MemberViewNotice />} />
    ) : (
      children
    )

  return (
    <>
      <MemberViewPanel memberId={memberId} />
      {children}
    </>
  )
}

export default MemberView
